/**
 * Manual de uma tarefa (leitura): cabeçalho (objetivo, quando, envolvidos,
 * sistemas, regras), passos numerados com avisos e trechos para copiar,
 * entrega final, erros comuns e pontos em aberto. No desktop, sumário por
 * seções ao lado. "Executar esta tarefa" sempre à mão.
 *
 * Link com `#passo-<chave>` (vindo da Busca) rola até o passo.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiEdit3, FiLayers, FiPlay } from "react-icons/fi";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { EmptyStateErro } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { ehFalhaDeRede, lerTarefa } from "../../services/areaPcp";
import { BASE, IndicadorConexao } from "./Casca";
import IniciarExecucao from "./componentes/IniciarExecucao";
import PassoConteudo, { Markdown } from "./componentes/PassoConteudo";
import { dataHoraLocal } from "./dominio/formatos";
import { useAreaPcp } from "./offline/AreaPcpContext";
import {
  Aviso,
  BlocoTitulo,
  BotaoPrimario,
  BotaoSecundario,
  Controles,
  LinhaTitulo,
  Meta,
  Pagina,
  Secao,
  Selo,
  Topo,
  desktop,
} from "./styles";
import { PERM_EDITAR, PERM_EXECUTAR } from "./permissoes";
import VersoesPainel from "./VersoesPainel";

const Colunas = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;

  @media ${desktop} {
    grid-template-columns: 220px minmax(0, 1fr);
  }
`;

const Sumario = styled.nav`
  display: none;

  @media ${desktop} {
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: sticky;
    top: calc(var(--app-header-height, 0px) + 96px);
    align-self: start;
  }

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--ink-3);
    margin-bottom: 6px;
  }

  a {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 13px;
    color: var(--ink-2);
    text-decoration: none;
  }

  a:hover {
    background: var(--surface-2);
    color: var(--ink);
  }

  small {
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
  }
`;

const Leitura = styled.article`
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 760px;
  min-width: 0;

  .objetivo {
    font-size: 16px;
    line-height: 1.55;
    color: var(--ink);
    margin: 0;
  }
`;

const Ficha = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px 16px;
  margin: 0;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);

  dt {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--ink-3);
  }
  dd {
    margin: 2px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--ink);
  }
  dd p,
  dd ul {
    margin: 0;
  }
`;

const ListaDePassos = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  > li {
    padding: 14px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
    scroll-margin-top: calc(var(--app-header-height, 0px) + 110px);
  }

  > li:target {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-ring);
  }
`;

const TituloDeSecao = styled.h3`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-2);
  scroll-margin-top: calc(var(--app-header-height, 0px) + 110px);
`;

const CAMPOS_DA_FICHA = [
  ["quando", "QUANDO"],
  ["envolvidos", "ENVOLVIDOS"],
  ["sistemas", "SISTEMAS"],
  ["regras", "REGRAS"],
];
const CAMPOS_DO_FIM = [
  ["entrega_final", "ENTREGA FINAL"],
  ["erros_comuns", "ERROS COMUNS"],
  ["em_aberto", "EM ABERTO"],
];

/** Passos agrupados por seção, na ordem; passos sem seção formam um grupo sem título. */
export function agruparPorSecao(tarefa) {
  const titulo = Object.fromEntries((tarefa.secoes || []).map((s, i) => [s.chave, { ...s, indice: i + 1 }]));
  const grupos = [];
  (tarefa.passos || []).forEach((passo) => {
    const secao = passo.secao ? titulo[passo.secao] : null;
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && (ultimo.secao?.chave || null) === (secao?.chave || null)) ultimo.passos.push(passo);
    else grupos.push({ secao, passos: [passo] });
  });
  return grupos;
}

function useTarefa(id) {
  const { request } = useAuth();
  const { pacote } = useAreaPcp();
  const [estado, setEstado] = useState({ carregando: true, tarefa: null, erro: null, offline: false });

  useEffect(() => {
    let ativo = true;
    setEstado((atual) => ({ ...atual, carregando: true }));
    lerTarefa(request, id)
      .then((tarefa) => ativo && setEstado({ carregando: false, tarefa, erro: null, offline: false }))
      .catch((erro) => {
        if (!ativo) return;
        if (ehFalhaDeRede(erro)) setEstado({ carregando: false, tarefa: null, erro: null, offline: true });
        else setEstado({ carregando: false, tarefa: null, erro, offline: false });
      });
    return () => {
      ativo = false;
    };
  }, [request, id]);

  const doPacote = estado.offline ? (pacote?.tarefas || []).find((t) => t.id === id) : null;
  return { ...estado, tarefa: estado.tarefa || doPacote, setTarefa: (tarefa) => setEstado((a) => ({ ...a, tarefa })) };
}

function TrechoReferenciado({ referencia }) {
  if (!referencia) return null;
  return (
    <Aviso $tom="info">
      <span>
        Passos {referencia.de} a {referencia.ate} de{" "}
        <Link to={`${BASE}/tarefas/${referencia.tarefa_id}`}>{referencia.tarefa_titulo}</Link> — na execução viram um
        sub-checklist aqui.
      </span>
    </Aviso>
  );
}

export default function Manual() {
  const { id } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { tarefa, carregando, erro, offline, setTarefa } = useTarefa(id);
  const [iniciar, setIniciar] = useState(false);
  const [versoes, setVersoes] = useState(false);
  const grupos = useMemo(() => (tarefa ? agruparPorSecao(tarefa) : []), [tarefa]);
  const podeEditar = hasPermission(PERM_EDITAR);

  useEffect(() => {
    if (!tarefa || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [tarefa, hash]);

  if (carregando && !tarefa) return <PageLoader scope="kpis" />;
  if (erro) return <EmptyStateErro erro={erro} titulo="Não foi possível abrir o manual" />;
  if (!tarefa) return <EmptyStateErro erro={{ message: "Este manual não está guardado neste aparelho. Conecte-se para abrir." }} />;

  const comSecoes = grupos.some((g) => g.secao);

  return (
    <Pagina>
      <Topo>
        <LinhaTitulo>
          <BotaoSecundario type="button" aria-label="Voltar para Tarefas" onClick={() => navigate(`${BASE}/tarefas`)} style={{ width: 34, padding: 0 }}>
            <FiArrowLeft size={16} aria-hidden="true" />
          </BotaoSecundario>
          <BlocoTitulo>
            <h1>{tarefa.titulo}</h1>
            <p>
              {tarefa.passos.length} passos · versão {tarefa.versao_atual}
              {tarefa.n_abertas || tarefa.execucoes_abertas?.total ? ` · ${tarefa.execucoes_abertas?.total ?? tarefa.n_abertas} abertas` : ""}
            </p>
          </BlocoTitulo>
          <Controles>
            <IndicadorConexao />
            {podeEditar && !offline ? (
              <>
                <BotaoSecundario type="button" onClick={() => setVersoes(true)} aria-label="Versões">
                  <FiLayers size={14} aria-hidden="true" />
                  <span className="so-desktop">Versões</span>
                </BotaoSecundario>
                <BotaoSecundario type="button" aria-label="Editar" onClick={() => navigate(`${BASE}/tarefas/${id}/editar`)}>
                  <FiEdit3 size={14} aria-hidden="true" />
                  <span>Editar</span>
                </BotaoSecundario>
              </>
            ) : null}
            {hasPermission(PERM_EXECUTAR) && tarefa.ativo !== false && !tarefa.apagada_em ? (
              <BotaoPrimario type="button" aria-label="Executar esta tarefa" onClick={() => setIniciar(true)}>
                <FiPlay size={14} aria-hidden="true" />
                <span>Executar</span>
                <span className="so-desktop"> esta tarefa</span>
              </BotaoPrimario>
            ) : null}
          </Controles>
        </LinhaTitulo>
      </Topo>

      {!tarefa.ativo && tarefa.ativo !== undefined ? (
        <Aviso $tom="neutral">Rascunho: só quem edita os manuais vê esta tarefa. Ative no editor quando estiver pronta.</Aviso>
      ) : null}

      <Colunas>
        {comSecoes ? (
          <Sumario aria-label="Sumário">
            <span>SUMÁRIO</span>
            {grupos.filter((g) => g.secao).map((g) => (
              <a key={g.secao.chave} href={`#secao-${g.secao.chave}`}>
                {g.secao.indice}. {g.secao.titulo}
                <small>
                  {g.passos[0].numero}–{g.passos[g.passos.length - 1].numero}
                </small>
              </a>
            ))}
          </Sumario>
        ) : <div />}
        <Leitura>
          {tarefa.objetivo ? <Markdown texto={tarefa.objetivo} className="objetivo" /> : null}
          {CAMPOS_DA_FICHA.some(([campo]) => tarefa[campo]) ? (
            <Ficha>
              {CAMPOS_DA_FICHA.filter(([campo]) => tarefa[campo]).map(([campo, rotulo]) => (
                <div key={campo}>
                  <dt>{rotulo}</dt>
                  <dd><Markdown texto={tarefa[campo]} /></dd>
                </div>
              ))}
            </Ficha>
          ) : null}

          <Secao>
            <h2>Passos</h2>
            {grupos.map((grupo) => (
              <React.Fragment key={grupo.secao?.chave || `sem-${grupo.passos[0].chave}`}>
                {grupo.secao ? (
                  <TituloDeSecao id={`secao-${grupo.secao.chave}`}>
                    {grupo.secao.indice}. {grupo.secao.titulo}
                  </TituloDeSecao>
                ) : null}
                <ListaDePassos>
                  {grupo.passos.map((passo) => (
                    <li key={passo.chave} id={`passo-${passo.chave}`}>
                      <PassoConteudo passo={passo}>
                        <TrechoReferenciado referencia={passo.referencia_exibicao} />
                      </PassoConteudo>
                    </li>
                  ))}
                </ListaDePassos>
              </React.Fragment>
            ))}
          </Secao>

          {CAMPOS_DO_FIM.filter(([campo]) => tarefa[campo]).map(([campo, rotulo]) => (
            <Secao key={campo}>
              <h2>{rotulo}</h2>
              <Markdown texto={tarefa[campo]} />
            </Secao>
          ))}

          <Meta>
            Atualizado por {tarefa.atualizado_por || "manual inicial"} · {dataHoraLocal(tarefa.atualizado_em)} · versão{" "}
            {tarefa.versao_atual}
            {tarefa.exige_identificador ? <Selo $tom="neutral">pede {tarefa.rotulo_identificador.toLowerCase()} ao começar</Selo> : null}
          </Meta>
        </Leitura>
      </Colunas>

      <IniciarExecucao
        tarefa={{ ...tarefa, n_passos: tarefa.passos.length }}
        aberta={iniciar}
        onFechar={() => setIniciar(false)}
      />
      {podeEditar && !offline ? (
        <VersoesPainel
          tarefa={tarefa}
          aberto={versoes}
          onFechar={() => setVersoes(false)}
          onRestaurada={(restaurada) => {
            setTarefa(restaurada);
            setVersoes(false);
          }}
        />
      ) : null}
    </Pagina>
  );
}
